import { UserInfo as User } from './auth';
export interface BasicUser {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  displayName?: string;
  blocked?: boolean;
  deactivated?: boolean;
}

// Role information
export interface Role {
  name: string;
  value: number;  
}

// Member structure (user with role information)
export interface Member {
  uuid: string;
  // For group context. Some call sites provide a minimal user object; accept both.
  user?: User | BasicUser;
  firstName?: string; // For global context
  lastName?: string;
  displayName?: string;
  profileImage?: string;
  role: Role;
  deactivated?: boolean;
  blocked?: boolean;
  joinedAt?: string;
}

// Member for global chat context (flattened structure)
export interface GlobalMember extends User {
  role: Role;
}

// API response structure for building members
export interface BuildingMembersResponse {
  members: Member[];
  totalCount: number;
  page: number;
  hasMore: boolean;
}

// API response structure for group members
export interface GroupMembersResponse {
  members: Member[];
  totalCount: number;
  page: number;
  hasMore: boolean;
}

// Props for individual member avatar
export interface MemberAvatarProps {
  member: Member | GlobalMember;
  index: number;
  size?: number;
  isGlobalChat?: boolean;
  onPress?: (member: Member | GlobalMember) => void;
}

// Props for the overflow indicator (+X more)
export interface MemberOverflowProps {
  count: number;
  index: number;
  size?: number;
  isGlobalChat?: boolean;
  onPress?: () => void;
}

// Props for the main GroupMembers component
export interface GroupMembersProps {
  // Data sources (provide either external data or IDs for fetching)
  members?: Member[] | GlobalMember[];
  totalCount?: number;

  // Context configuration
  buildingId?: string;
  groupId?: string;
  isGlobalChat?: boolean;
  isWelcomeMode?: boolean;

  // Display configuration
  maxDisplay?: number;
  size?: number;
  showViewAllButton?: boolean;

  // Loading states
  loading?: boolean;
  error?: string;

  // Callbacks
  onShowAllMembers?: () => void;
  onMemberPress?: (member: Member | GlobalMember) => void;
  onRefresh?: () => void;

  // Customization
  // emptyState?: React.ReactNode;
  // loadingState?: React.ReactNode;

}

// Hook configuration for data fetching
export interface UseGroupMembersConfig {
  buildingId?: string;
  groupId?: string;
  enabled?: boolean;
  maxDisplay?: number;
  prioritizePictures?: boolean;
  filterResidentsOnly?: boolean;
  staleTime?: number;
}

// Hook return type
export interface UseGroupMembersReturn {
  // Data
  members: Member[] | GlobalMember[];
  displayMembers: Member[] | GlobalMember[];
  totalCount: number;
  visibleCount: number;
  overflowCount: number;

  // States
  isLoading: boolean;
  isError: boolean;
  error: string | null;

  // Actions
  refetch: () => void;

  // Computed values
  hasMembers: boolean;
  hasOverflow: boolean;
  isEmpty: boolean;
}

// Error types
export enum GroupMembersErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  NOT_FOUND = 'NOT_FOUND',
  INVALID_CONFIG = 'INVALID_CONFIG',
}

export interface GroupMembersError {
  type: GroupMembersErrorType;
  message: string;
  details?: any;
}

// Analytics event data
export interface GroupMembersAnalytics {
  buildingId?: string;
  groupId?: string;
  memberCount: number;
  displayCount: number;
  hasOverflow: boolean;
  isGlobalChat: boolean;
  source: 'chat' | 'group' | 'event';
}

// Constants
export const GROUP_MEMBERS_CONSTANTS = {
  // Display limits
  DEFAULT_MAX_DISPLAY: 8,
  WELCOME_MAX_DISPLAY: 5,

  // Avatar sizing
  DEFAULT_AVATAR_SIZE: 35,
  CAPTAIN_SIZE_BONUS: 2,

  // Layout and positioning
  OVERLAP_OFFSET: -45,
  Z_INDEX_BASE: 1000,

  // UI styling
  ACTIVE_OPACITY: 0.8,
  BORDER_WIDTH: 2,

  // Text and font sizing
  MAX_OVERFLOW_DISPLAY: 99,
  SMALL_FONT_SIZE_THRESHOLD: 24,
  SMALL_FONT_SIZE: '$0', // 10px
  DEFAULT_FONT_SIZE: '$1', // 12px

  // Icon sizing
  CHEVRON_ICON_SIZE: 20,

  // Shimmer component
  SHIMMER: {
    DEFAULT_HEIGHT: 20,
    OPACITY: 0.6,
  },

  // Data caching
  STALE_TIME: 5 * 60 * 1000, // 5 minutes

  // Colors (using theme tokens)
  COLORS: {
    GLOBAL_CHAT: {
      BACKGROUND: '$colorLink',
      TEXT: '$white',
    },
    GROUP_CHAT: {
      BACKGROUND: '$cobuDarkBlue25',
      TEXT: '$cobuDarkBlue',
    },
    AVATAR_FALLBACK: {
      BACKGROUND: '$cobuDarkBlue25',
      TEXT: '$cobuDarkBlue',
    },
  },
} as const;

export default GroupMembersProps; 